import { createContext, memo, useContext, useEffect } from 'react';
import { BehaviorSubject, distinctUntilChanged, skip } from 'rxjs';
import useBehaviorSubject from '../../functions/hooks/useBehaviorSubject';
import { useCreation } from '../../functions/hooks/useCreation';
import { isWeb } from '../../utils/deviceInfo';
import { FCC } from '../../utils/react';

export type AudioControllerContextProps = {
  muted$: BehaviorSubject<boolean>;
  play$: BehaviorSubject<boolean>;
};

export const AudioControllerContext =
  createContext<AudioControllerContextProps>(null as any);

export const useAudioController = () => useContext(AudioControllerContext);

export const useSafeAudioController = () =>
  useContext(AudioControllerContext) as AudioControllerContextProps | undefined;

export const useMuteState = () => {
  const { muted$ } = useAudioController();
  return useBehaviorSubject(muted$);
};

/**
 * A context controller that is used to control cascade audio contexts so
 * that we can manage current video/audio playing back more consistently.
 * In the future, we will implement cross context audio playback coordination.
 */
export const AudioController: FCC = memo(props => {
  const parentController = useSafeAudioController();

  const play$ = useCreation(() => new BehaviorSubject(true), []);
  const muted$ = useCreation(
    // initialized to the mute state of parent controller by default.
    () => new BehaviorSubject(parentController?.muted$.value ?? isWeb),
    [parentController?.muted$.value],
  );

  useEffect(() => {
    if (parentController?.muted$ == null) return;

    let isParentUnmuted = !parentController.muted$.value;
    let shouldRestoreParentToUnmutedState = isParentUnmuted;

    if (isParentUnmuted) {
      // mute the parent controller if we start with unmuted in this context.
      parentController.muted$.next(true);
    }
    const muteSub = muted$
      .pipe(distinctUntilChanged(), skip(1))
      .subscribe(muted => {
        if (muted) {
          // if the user has opt-in to mute, we will tell the parent to mute
          // as well to honor user's preference consistently.
          if (!parentController.muted$.value) {
            parentController.muted$.next(true);
          }
          shouldRestoreParentToUnmutedState = false;
        } else if (isParentUnmuted && !shouldRestoreParentToUnmutedState) {
          // if the parent is initially unmuted, the user has changed from
          // muted back to unmuted, we're going to restore the parent state.
          shouldRestoreParentToUnmutedState = true;
        }
      });

    const playSub = play$
      .pipe(distinctUntilChanged(), skip(1))
      .subscribe(play => {
        if (play) {
          isParentUnmuted = !parentController.muted$.value;
          shouldRestoreParentToUnmutedState = isParentUnmuted;
          if (isParentUnmuted) {
            // mute the parent controller if we start with unmuted in this context.
            parentController.muted$.next(true);
          }
        } else {
          if (isParentUnmuted && shouldRestoreParentToUnmutedState) {
            // restore the parent controller to be unmuted
            parentController.muted$.next(false);
          }
        }
      });

    return () => {
      muteSub.unsubscribe();
      playSub.unsubscribe();

      if (isParentUnmuted && shouldRestoreParentToUnmutedState) {
        // restore the parent controller to be unmuted on unmount
        parentController.muted$.next(false);
      }
    };
  }, [parentController?.muted$, muted$, play$]);

  return (
    <AudioControllerContext.Provider value={{ play$, muted$ }}>
      {props.children}
    </AudioControllerContext.Provider>
  );
});
